import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Eye, EyeOff, Lock, User, Mail, CheckCircle, XCircle,
  Globe, ChevronDown, HelpCircle, Loader2, ArrowRight, Sparkles,
} from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { GoogleLogin } from '@react-oauth/google';
import { getDeviceFp } from '../utils/deviceFp';
import AuthBrandShowcase from './AuthBrandShowcase';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Páginas legais vivem no site de vendas (landing), em darkplanner.com.br.
const LEGAL_BASE = 'https://darkplanner.com.br';

const LINKS = {
  discord: 'https://discord.gg/uV9Y8YvW',
  instagram: 'https://www.instagram.com/oficialdarkplanner?igsh=emRtN2I3cGhsd2c=',
  support: 'https://wa.me/5577988744698?text=Ol%C3%A1!%20Preciso%20de%20suporte%20com%20o%20Dark%20Planner.',
};

// ════════════════════════════════════════════════════════════════
// SVG Icons (marca)
// ════════════════════════════════════════════════════════════════
const DiscordIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const InstagramIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const LANGUAGES = [
  { code: 'pt', label: 'PT', flag: '🇧🇷' },
  { code: 'en', label: 'EN', flag: '🇺🇸' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
];

// Textura de grão (fractal noise) — igual ao corpo da landing (opacity baixa).
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

// ════════════════════════════════════════════════════════════════
// Language Selector (top-right corner)
// ════════════════════════════════════════════════════════════════
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 30 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '10px',
          color: '#8A8A8A',
          fontSize: '13px', fontWeight: 500,
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
          fontFamily: "'Outfit', sans-serif",
          transition: 'all 0.2s',
        }}
      >
        <Globe size={14} />
        <span>{current.flag} {current.label}</span>
        <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: '#141414',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px', padding: '4px',
            minWidth: '120px', zIndex: 51,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(12px)',
          }}>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '8px 12px',
                  border: 'none', borderRadius: '6px',
                  background: i18n.language === lang.code ? 'rgba(13,174,255,0.15)' : 'transparent',
                  color: i18n.language === lang.code ? '#0DAEFF' : '#E5E7EB',
                  fontSize: '13px',
                  fontWeight: i18n.language === lang.code ? 600 : 400,
                  cursor: 'pointer', transition: 'background 0.15s',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN LOGIN COMPONENT
// ════════════════════════════════════════════════════════════════
const Login = ({ onLogin }) => {
  usePageTitle('Login');
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [animReady, setAnimReady] = useState(false);

  // Load saved email & URL params
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const email = params.get('email');
    if (tab === 'cadastro') setIsRegister(true);
    if (email) {
      setFormData(prev => ({ ...prev, email }));
      setTimeout(() => checkEmailAuthorization(email), 500);
    }
    requestAnimationFrame(() => setAnimReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkEmailAuthorization = async (email) => {
    if (!email || !email.includes('@')) { setEmailCheckStatus(null); return; }
    setEmailCheckStatus('checking');
    try {
      const response = await axios.post(`${API}/auth/check-email`, { email });
      setEmailCheckStatus(response.data.authorized ? 'authorized' : 'unauthorized');
      setEmailCheckMessage(response.data.message);
    } catch {
      setEmailCheckStatus('unauthorized');
      setEmailCheckMessage(t('login.error_check_email'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) { toast.error(t('login.error_min_password')); return; }
    if (isRegister && formData.password !== formData.confirmPassword) { toast.error(t('login.error_password_mismatch')); return; }
    if (isRegister && emailCheckStatus !== 'authorized') { toast.error(t('login.error_email_unauthorized')); return; }
    if (isRegister && !termsAccepted) { toast.error(t('login.error_terms')); return; }

    setLoading(true);
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister ? formData : { email: formData.email, password: formData.password };
      if (isRegister) {
        await axios.post(`${API}${endpoint}`, payload);
        toast.success(t('login.success_register'));
        setIsRegister(false);
        setFormData({ email: '', password: '', full_name: '', confirmPassword: '' });
        setEmailCheckStatus(null);
        setTermsAccepted(false);
      } else {
        const response = await axios.post(`${API}${endpoint}`, payload);
        if (rememberMe) { localStorage.setItem('rememberedEmail', formData.email); }
        else { localStorage.removeItem('rememberedEmail'); }
        toast.success(t('login.success_login'));
        onLogin(response.data.access_token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('login.error_generic'));
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email' && isRegister) {
      if (value && value.includes('@')) { setTimeout(() => checkEmailAuthorization(value), 500); }
      else { setEmailCheckStatus(null); }
    }
  };

  const handleFocus = (e) => {
    e.target.parentElement.style.borderColor = '#0DAEFF';
    e.target.parentElement.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)';
  };
  const handleBlur = (e) => {
    e.target.parentElement.style.borderColor = 'rgba(255,255,255,0.08)';
    e.target.parentElement.style.boxShadow = 'none';
  };

  const switchMode = () => {
    setIsRegister(!isRegister);
    setEmailCheckStatus(null);
    setEmailCheckMessage('');
  };

  const isDisabled = loading || (isRegister && emailCheckStatus !== 'authorized');

  // ─── Shared styles (DNA da landing) ───
  const inputWrapperStyle = {
    display: 'flex', alignItems: 'center',
    background: 'rgba(10,10,10,0.6)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '0 12px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };
  const inputStyle = {
    flex: 1, background: 'transparent', border: 'none',
    padding: '13px 8px', color: '#F5F5F5',
    fontSize: '14px', outline: 'none',
    fontFamily: "'Outfit', sans-serif",
  };
  const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: 600,
    color: '#8A8A8A', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };

  return (
    <div className="dp-login-page" style={{
      minHeight: '100vh', position: 'relative',
      background: '#0A0A0A',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      overflow: 'hidden',
    }}>
      {/* Fundo: #0A0A0A + grão + glow (igual corpo da landing) */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN_URI, opacity: 0.04 }} />
      </div>

      <LanguageSelector />

      {/* ═══════ SHELL (dois painéis que trocam de lado) ═══════ */}
      <div className={`dp-login-shell${isRegister ? ' is-register' : ''}`}>

        {/* ─────── PAINEL DE MARCA ─────── */}
        <aside className="dp-brand-panel">
          <div className="dp-brand-glow" />
          <div className="dp-brand-inner" style={{
            opacity: animReady ? 1 : 0,
            transform: animReady ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            <div className="dp-brand-logo">
              <img src="/logo-darkplanner.png" alt="Dark Planner" />
              <span>Dark Planner</span>
            </div>

            <h1 className="dp-brand-title">{t('login.slogan')}</h1>

            <AuthBrandShowcase />

            <div className="dp-brand-trusted">
              <Sparkles size={13} style={{ color: '#0DAEFF' }} />
              {t('login.trusted_by')}
            </div>

            <div className="dp-brand-socials">
              <a href={LINKS.discord} target="_blank" rel="noopener noreferrer" aria-label="Discord"><DiscordIcon size={17} /></a>
              <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><InstagramIcon size={17} /></a>
              <a href={LINKS.support} target="_blank" rel="noopener noreferrer" aria-label="Suporte"><HelpCircle size={18} /></a>
            </div>
          </div>
        </aside>

        {/* ─────── PAINEL DE FORMULÁRIO ─────── */}
        <main className="dp-form-panel">
          <div className="dp-form-card" style={{
            opacity: animReady ? 1 : 0,
            transform: animReady ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s',
          }}>
            {/* Logo compacto (aparece no mobile, escondido no desktop via CSS) */}
            <div className="dp-form-logo">
              <img src="/logo-darkplanner.png" alt="Dark Planner" />
            </div>

            <h2 className="dp-form-title">
              {isRegister ? t('login.subtitle_register') : t('login.subtitle_login')}
            </h2>
            <p className="dp-form-sub">
              {isRegister ? t('login.register_sub') : t('login.login_sub')}
            </p>

            {/* Google (mantido — fluxo intacto) */}
            {GOOGLE_CLIENT_ID && (
              <>
                <div className="dp-google-wrap">
                  <GoogleLogin
                    onSuccess={async (credentialResponse) => {
                      try {
                        const deviceFp = await getDeviceFp();  // ETAPA 3: antifraude do free
                        const res = await axios.post(`${API}/auth/google`, {
                          credential: credentialResponse.credential,
                          device_fp: deviceFp || undefined,
                        });
                        onLogin(res.data.access_token);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Falha ao entrar com Google');
                      }
                    }}
                    onError={() => toast.error('Falha ao entrar com Google')}
                    theme="filled_black"
                    text="continue_with"
                    shape="pill"
                    width="320"
                  />
                </div>
                <div className="dp-divider">
                  <span />
                  <span className="dp-divider-text">{t('login.or') || 'ou'}</span>
                  <span />
                </div>
              </>
            )}

            {/* ═══════ FORM ═══════ */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

              {/* Email (register) */}
              {isRegister && (
                <div>
                  <label style={labelStyle}>{t('login.email_required')} <span style={{ color: '#EF4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <Mail size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                    <input
                      type="email" name="email" required
                      value={formData.email} onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (isRegister && formData.email && formData.email.includes('@') && emailCheckStatus !== 'authorized' && emailCheckStatus !== 'checking') {
                          checkEmailAuthorization(formData.email);
                        }
                      }}
                      placeholder={t('login.email_placeholder')}
                      style={inputStyle}
                    />
                    {emailCheckStatus === 'checking' && (
                      <Loader2 size={16} style={{ color: '#0DAEFF', flexShrink: 0, animation: 'dp-login-spin-loader 1s linear infinite' }} />
                    )}
                    {emailCheckStatus === 'authorized' && <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} />}
                    {emailCheckStatus === 'unauthorized' && <XCircle size={16} style={{ color: '#EF4444', flexShrink: 0 }} />}
                  </div>
                  {emailCheckMessage && (
                    <p style={{ fontSize: '11px', margin: '5px 0 0 0', color: emailCheckStatus === 'authorized' ? '#10B981' : '#EF4444' }}>{emailCheckMessage}</p>
                  )}
                </div>
              )}

              {/* Full Name (register) */}
              {isRegister && (
                <div>
                  <label style={labelStyle}>{t('login.full_name')} <span style={{ color: '#EF4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <User size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                    <input
                      type="text" name="full_name" required
                      value={formData.full_name} onChange={handleChange}
                      onFocus={(e) => {
                        handleFocus(e);
                        if (formData.email && formData.email.includes('@') && emailCheckStatus !== 'authorized' && emailCheckStatus !== 'checking') {
                          checkEmailAuthorization(formData.email);
                        }
                      }}
                      onBlur={handleBlur}
                      placeholder={t('login.full_name_placeholder')}
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}

              {/* Email (login) */}
              {!isRegister && (
                <div>
                  <label style={labelStyle}>{t('login.email')}</label>
                  <div style={inputWrapperStyle}>
                    <Mail size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                    <input
                      type="email" name="email" required
                      value={formData.email} onChange={handleChange}
                      onFocus={handleFocus} onBlur={handleBlur}
                      placeholder={t('login.email_placeholder')}
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}

              {/* Password */}
              <div>
                <label style={labelStyle}>{t('login.password')}</label>
                <div style={inputWrapperStyle}>
                  <Lock size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password" required
                    value={formData.password} onChange={handleChange}
                    onFocus={handleFocus} onBlur={handleBlur}
                    placeholder={t('login.password_placeholder')}
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', padding: '4px' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {isRegister && (
                  <p style={{ fontSize: '11px', margin: '4px 0 0 0', color: '#6B7280' }}>{t('login.password_hint')}</p>
                )}
              </div>

              {/* Confirm Password (register) */}
              {isRegister && (
                <div>
                  <label style={labelStyle}>{t('login.confirm_password')} <span style={{ color: '#EF4444' }}>*</span></label>
                  <div style={inputWrapperStyle}>
                    <Lock size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                    <input
                      type="password" name="confirmPassword" required
                      value={formData.confirmPassword} onChange={handleChange}
                      onFocus={handleFocus} onBlur={handleBlur}
                      placeholder={t('login.password_placeholder')}
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}

              {/* Remember Me + Forgot Password (login) */}
              {!isRegister && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                      style={{ width: '15px', height: '15px', marginRight: '8px', cursor: 'pointer', accentColor: '#0DAEFF' }} />
                    <span style={{ fontSize: '12px', color: '#8A8A8A' }}>{t('login.remember_me')}</span>
                  </label>
                  <a href="/forgot-password" style={{ fontSize: '12px', color: '#0DAEFF', textDecoration: 'none', fontWeight: 500 }}>
                    {t('login.forgot_password')}
                  </a>
                </div>
              )}

              {/* Aceite de termos (register) */}
              {isRegister && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                    style={{ width: '15px', height: '15px', marginTop: '2px', cursor: 'pointer', accentColor: '#0DAEFF', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#8A8A8A', lineHeight: 1.5 }}>
                    {t('login.terms_agree_pre')}{' '}
                    <a href={`${LEGAL_BASE}/terms`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#0DAEFF', textDecoration: 'none', fontWeight: 600 }}>
                      {t('login.terms_of_service')}
                    </a>{' '}{t('login.terms_and')}{' '}
                    <a href={`${LEGAL_BASE}/privacy`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#0DAEFF', textDecoration: 'none', fontWeight: 600 }}>
                      {t('login.privacy_policy')}
                    </a>.
                  </span>
                </label>
              )}

              {/* Submit (estilo dp-btn-primary) */}
              <button type="submit" disabled={isDisabled} className="dp-login-cta"
                style={{
                  width: '100%', padding: '14px',
                  background: isDisabled ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                  color: isDisabled ? '#6B7280' : '#fff',
                  border: 'none', borderRadius: '12px',
                  fontSize: '15px', fontWeight: 600,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                  opacity: isDisabled ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: 'none',
                  letterSpacing: '0.2px', fontFamily: "'Outfit', sans-serif",
                  marginTop: '4px',
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'dp-login-spin-loader 1s linear infinite' }} />
                    {t('login.btn_processing')}
                  </>
                ) : (
                  <>
                    {isRegister ? t('login.btn_register') : t('login.btn_login')}
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>

            {/* Switch mode */}
            <p style={{ textAlign: 'center', marginTop: '18px', fontSize: '13px', color: '#8A8A8A' }}>
              {isRegister ? t('login.has_account') : t('login.no_account')}{' '}
              <button type="button" onClick={switchMode}
                style={{ background: 'none', border: 'none', color: '#0DAEFF', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: "'Outfit', sans-serif", padding: 0 }}>
                {isRegister ? t('login.btn_login') : t('login.create_account')}
              </button>
            </p>

            {/* Consentimento (login) — no registro o aceite é o checkbox acima */}
            {!isRegister && (
              <p className="dp-consent">
                {t('login.consent_pre')}{' '}
                <a href={`${LEGAL_BASE}/terms`} target="_blank" rel="noopener noreferrer">{t('login.terms_of_use')}</a>{' '}
                {t('login.terms_and')}{' '}
                <a href={`${LEGAL_BASE}/privacy`} target="_blank" rel="noopener noreferrer">{t('login.privacy_policy')}</a>.
              </p>
            )}
          </div>
        </main>
      </div>

      {/* Rodapé da página */}
      <footer className="dp-page-foot">
        <span>{t('login.copyright')}</span>
        <span className="dp-page-foot-links">
          <a href={`${LEGAL_BASE}/privacy`} target="_blank" rel="noopener noreferrer">{t('login.privacy_policy')}</a>
          <span style={{ opacity: 0.4 }}>·</span>
          <a href={`${LEGAL_BASE}/terms`} target="_blank" rel="noopener noreferrer">{t('login.terms_of_service')}</a>
        </span>
      </footer>

      {/* ═══════ ESTILOS ═══════ */}
      <style>{`
        @keyframes dp-login-spin-loader { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .dp-login-page input::placeholder { color: #4B5563 !important; }
        .dp-login-cta:hover:not(:disabled) { background: linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%) !important; transform: translateY(-1px); }
        .dp-login-cta svg:last-child { transition: transform 0.2s cubic-bezier(0.4,0,0.2,1); }
        .dp-login-cta:hover:not(:disabled) svg:last-child { transform: rotate(-45deg); }

        /* Shell: dois painéis 50/50 que deslizam (troca de lado no registro) */
        .dp-login-shell { position: relative; width: 100%; min-height: 100vh; z-index: 10; }
        .dp-brand-panel, .dp-form-panel {
          position: absolute; top: 0; bottom: 0; left: 0; width: 50%;
          display: flex; align-items: center; justify-content: center;
          padding: 40px; box-sizing: border-box;
          transition: transform 0.5s cubic-bezier(0.4,0,0.2,1);
        }
        .dp-brand-panel { transform: translateX(0%); overflow: hidden; }
        .dp-form-panel  { transform: translateX(100%); overflow-y: auto; padding-bottom: 88px; }
        .dp-login-shell.is-register .dp-brand-panel { transform: translateX(100%); }
        .dp-login-shell.is-register .dp-form-panel  { transform: translateX(0%); }

        /* Painel de marca */
        .dp-brand-glow {
          position: absolute; top: 50%; left: 50%; width: 120%; height: 80%;
          transform: translate(-50%, -50%);
          background: radial-gradient(ellipse, rgba(13,174,255,0.18) 0%, rgba(59,130,246,0.06) 40%, transparent 70%);
          filter: blur(60px); pointer-events: none;
        }
        .dp-brand-inner { position: relative; max-width: 420px; width: 100%; }
        .dp-brand-logo { display: inline-flex; align-items: center; gap: 12px; margin-bottom: 28px; }
        .dp-brand-logo img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; box-shadow: 0 8px 32px rgba(13,174,255,0.25); }
        .dp-brand-logo span { font-size: 22px; font-weight: 700; color: #F5F5F5; letter-spacing: -0.4px; }
        .dp-brand-title { font-size: 34px; font-weight: 700; color: #F5F5F5; line-height: 1.12; letter-spacing: -1px; margin: 0 0 24px 0; }
        .dp-brand-features { list-style: none; padding: 0; margin: 0 0 28px 0; display: flex; flex-direction: column; gap: 13px; }
        .dp-brand-features li { display: flex; align-items: center; gap: 10px; font-size: 14.5px; color: #B8B8B8; }
        .dp-brand-trusted { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 600; color: #0DAEFF; padding: 6px 13px; border-radius: 999px; background: rgba(13,174,255,0.08); border: 1px solid rgba(13,174,255,0.22); margin-bottom: 26px; }
        .dp-brand-socials { display: flex; gap: 10px; }
        .dp-brand-socials a { width: 38px; height: 38px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); transition: all 200ms ease; }
        .dp-brand-socials a:hover { color: #fff; transform: translateY(-2px); background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%); border-color: transparent; }

        /* Painel de formulário */
        .dp-form-card { width: 100%; max-width: 400px; }
        .dp-form-logo { display: flex; justify-content: center; margin-bottom: 14px; }
        .dp-form-logo img { width: 52px; height: 52px; border-radius: 14px; object-fit: cover; box-shadow: 0 8px 32px rgba(13,174,255,0.25); }
        .dp-form-title { font-size: 24px; font-weight: 700; color: #F5F5F5; margin: 0 0 4px 0; letter-spacing: -0.5px; text-align: center; }
        .dp-form-sub { font-size: 14px; color: #8A8A8A; margin: 0 0 24px 0; text-align: center; }
        .dp-google-wrap { display: flex; justify-content: center; }
        .dp-divider { display: flex; align-items: center; gap: 12px; margin: 18px 0; }
        .dp-divider span:first-child, .dp-divider span:last-child { flex: 1; height: 1px; background: rgba(255,255,255,0.08); }
        .dp-divider-text { font-size: 12px; color: #6B7280; }
        .dp-consent { text-align: center; margin: 16px 0 0; font-size: 11.5px; color: #555; line-height: 1.5; }
        .dp-consent a { color: #888; text-decoration: none; transition: color 200ms ease; }
        .dp-consent a:hover { color: #0DAEFF; }
        .dp-page-foot { position: absolute; bottom: 0; left: 0; right: 0; z-index: 12; display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; padding: 14px 20px; font-size: 11.5px; color: #555; }
        .dp-page-foot-links { display: inline-flex; align-items: center; gap: 8px; }
        .dp-page-foot a { color: #777; text-decoration: none; transition: color 200ms ease; }
        .dp-page-foot a:hover { color: #0DAEFF; }

        /* ─── Responsivo: empilha (formulário em foco, marca como topo) ─── */
        @media (max-width: 900px) {
          .dp-login-shell, .dp-login-shell.is-register { display: flex; flex-direction: column; position: static; min-height: 100vh; }
          .dp-brand-panel, .dp-form-panel,
          .dp-login-shell.is-register .dp-brand-panel, .dp-login-shell.is-register .dp-form-panel {
            position: relative; width: 100%; transform: none !important; padding: 28px 22px;
          }
          .dp-brand-panel { order: 1; min-height: auto; padding-top: 64px; }
          .dp-form-panel { order: 2; flex: 1; align-items: flex-start; padding-bottom: 76px; }
          .dp-brand-title { font-size: 24px; margin-bottom: 18px; }
          .dp-brand-features { display: none; }
          .dp-brand-trusted { margin-bottom: 0; }
          .dp-brand-socials { display: none; }
        }
        @media (max-width: 520px) {
          .dp-brand-panel { display: none; }
          .dp-form-panel { padding: 64px 18px 28px; align-items: flex-start; }
          .dp-form-logo { display: flex; }
          .dp-form-title { text-align: center; }
          .dp-form-sub { text-align: center; }
        }
      `}</style>
    </div>
  );
};

export default Login;
